import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseItem, useDatabaseData } from "@/components/page/hooks"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { MultiSelect } from "@/components/ui/multi-select"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { zodResolver } from "@hookform/resolvers/zod"
import { User, Info } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { useEffect } from "react"
import { supabase } from "@/lib/agents/db"
import { useQuery, useQueryClient } from "@tanstack/react-query"

export const users_crud = {
  title: "User",
  route: "/settings/users/:id",
  component,
  group: "settings",
  resource: "users",
  icon: User,
  action: "read",
} satisfies PageConfig

const createUserSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).optional(),
  first_name: z.string(),
  last_name: z.string(),
  status: z.string(),
  role: z.string(),
  teams: z.array(z.string()),
})

const editUserSchema = z.object({
  email: z.union([z.string(), z.null(), z.undefined()]).optional(),
  password: z.union([z.string(), z.null(), z.undefined()]).optional(),
  first_name: z.string(),
  last_name: z.string(),
  status: z.string(),
  role: z.string(),
  teams: z.array(z.string()),
})

type CreateUserData = z.infer<typeof createUserSchema>
type EditUserData = z.infer<typeof editUserSchema>
type FormData = CreateUserData | EditUserData

export function component() {
  const { data, id, loading, submit, toastSuccess, navigate, toast } = useDatabaseItem("users")
  const { data: teamsData, loading: teamsLoading } = useDatabaseData("teams")
  const { data: rolesData, loading: rolesLoading } = useDatabaseData("roles")
  const queryClient = useQueryClient()
  
  const { data: userWithTeamsData, isLoading: userWithTeamsLoading } = useQuery({
    queryKey: ['user-with-teams', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase.from('users_with_teams')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  const form = useForm<FormData>({
    resolver: zodResolver(id ? editUserSchema : createUserSchema),
    defaultValues: {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      status: 'active',
      role: '',
      teams: []
    },
  })

  useEffect(() => {
    if (userWithTeamsData) {
      form.reset({
        ...userWithTeamsData,
        teams: userWithTeamsData.team_ids || [],
        password: ''
      })
    } else if (data && id) {
      form.reset({
        ...data,
        teams: data.teams || [],
        password: ''
      })
    }
  }, [userWithTeamsData, data, id, form])


  const handleTeamUpdates = async (userId: string, currentTeamIds: string[], newTeamIds: string[]) => {
    const teamsToRemove = currentTeamIds.filter(teamId => !newTeamIds.includes(teamId))
    const teamsToAdd = newTeamIds.filter(teamId => !currentTeamIds.includes(teamId))

    if (teamsToRemove.length > 0) {
      await Promise.all(teamsToRemove.map(async (teamId) => {
        const { error } = await supabase
          .from("user_teams")
          .delete()
          .match({ user_id: userId, team_id: teamId })
        if (error) throw new Error(`Failed to remove team ${teamId}: ${error.message}`)
      }))
    }

    if (teamsToAdd.length > 0) {
      const { error } = await supabase
        .from("user_teams")
        .insert(teamsToAdd.map(teamId => ({ user_id: userId, team_id: teamId })))
      if (error) throw new Error(`Failed to add teams: ${error.message}`)
    }
  }

  async function onSubmit(formData: FormData) {

    if (id) {
      const emailToSync = formData.email || userWithTeamsData?.email || data?.email
      
      const userUpdateData: any = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        status: formData.status
      }
      
      if (emailToSync) {
        userUpdateData.email = emailToSync
      }
      
      const { error } = await supabase
        .from('users')
        .update(userUpdateData)
        .eq('id', id)
      
      if (error) {
        toast.error(`Update failed: ${error.message}`)
        return
      }

      // Handle team updates
      const currentTeamIds = userWithTeamsData?.team_ids || []
      await handleTeamUpdates(id, currentTeamIds, formData.teams)
      
      // Invalidate React Query cache to refresh the data
      await queryClient.invalidateQueries({ queryKey: ['user-with-teams', id] })
      
      toastSuccess()
      navigate("/settings/users")
    } else {
      // Create new user
      const { data: existingUser } = await supabase
        .from('users')
        .select('email')
        .eq('email', formData.email)
        .single()

      if (existingUser) {
        toast.error("A user with this email address already exists. Please use a different email.")
        return
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      })
      
      if (authError) {
        if (authError.message.includes('already registered')) {
          toast.error("This email address is already registered. Please use a different email.")
        } else if (authError.message.includes('invalid email')) {
          toast.error("Please enter a valid email address.")
        } else if (authError.message.includes('password')) {
          toast.error("Password must be at least 8 characters long.")
        } else {
          toast.error(`Authentication error: ${authError.message}`)
        }
        return
      }

      if (!authData.user) {
        toast.error("No user data returned from auth creation")
        return
      }

      const { error: userError } = await supabase.from('users')
        .upsert([{
          id: authData.user.id,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          status: formData.status || 'active',
        }]).select(`*`).single()

      if (userError) {
        // Clean up auth user if user creation fails
        await supabase.auth.signOut()
        toast.error(`User creation failed: ${userError.message}`)
        return
      }

      // Handle team assignments for new user
      if (formData.teams.length > 0) {
        await handleTeamUpdates(authData.user.id, [], formData.teams)
      }

      // Invalidate React Query cache to refresh the data
      await queryClient.invalidateQueries({ queryKey: ['user-with-teams', authData.user.id] })
      
      toastSuccess()
      navigate("/settings/users")
    }
  }

  async function onDelete() {
    if (!id) return
    const { error: deleteError } = await supabase
      .from('users')
      .delete()
      .eq('id', id)
    
    if (deleteError) {
      toast.error(`Failed to delete user: ${deleteError.message}`)
      return
    }

    toast.success("User deleted successfully")
  }

  if (loading || teamsLoading || rolesLoading || (id && userWithTeamsLoading)) {
    return <CRUDLoader />
  }

  const teamOptions = teamsData?.map(team => ({
    label: team.name || '',
    value: team.id || '',
  })) || []

  const roleOptions = rolesData?.map(role => ({
    label: role.name || '',
    value: role.id || '',
    description: role.description || '',
    permissions: role.permissions || [],
  })) || []

  const getRolePermissionsDescription = (permissions: any[]) => {
    if (!permissions || permissions.length === 0) {
      return "No permissions configured"
    }

    const resourceMap: Record<string, { create: boolean; read: boolean; update: boolean; delete: boolean; share: boolean }> = {}
    
    permissions.forEach((perm: any) => {
      if (perm.resource) {
        resourceMap[perm.resource] = {
          create: perm.create || false,
          read: perm.read || false,
          update: perm.update || false,
          delete: perm.delete || false,
          share: perm.share || false,
        }
      }
    })

    const resourceLabels: Record<string, string> = {
      agents: 'Agents',
      templates: 'Templates',
      bots: 'Bots',
      prompts: 'System Prompts',
      playground: 'Playground',
      dashboard: 'Dashboard',
      hitl: 'Moderation',
      evaluation: 'Simulator',
      logs: 'Logs',
      scores: 'Scores',
      collections: 'Collections',
      sources: 'Data Sources',
      projects: 'Projects',
      teams: 'Teams',
      billing: 'Billing',
      usage: 'Usage',
      roles: 'Roles',
      users: 'Users',
      apikeys: 'API Keys',
      models: 'Models',
    }

    const permissionDescriptions: string[] = []
    
    Object.entries(resourceMap).forEach(([resource, perms]) => {
      const actions: string[] = []
      if (perms.create) actions.push('Create')
      if (perms.read) actions.push('Read')
      if (perms.update) actions.push('Update')
      if (perms.delete) actions.push('Delete')
      if (perms.share) actions.push('Share')
      
      if (actions.length > 0) {
        permissionDescriptions.push(`${resourceLabels[resource] || resource}: ${actions.join(', ')}`)
      }
    })

    return permissionDescriptions.length > 0 
      ? permissionDescriptions.join('\n')
      : "No permissions configured"
  }



  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="users" onSuccess={"/settings/users"} onDelete={onDelete} />
          </PageTitle>

          <div className="rgrid">
            <div className="row">
              {!id && (
                <>
                  <div className="rc-6">
                    <FormField control={form.control} name="email" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl><Input {...field} type="email" /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <div className="rc-6">
                    <FormField control={form.control} name="password" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl><Input {...field} type="password" /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </>
              )}
              <div className="rc-6">
                <FormField control={form.control} name="first_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-6">
                <FormField control={form.control} name="last_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-3">
                <FormField control={form.control} name="status" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-3">
                <FormField control={form.control} name="role" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="inline-flex items-center gap-1.5">
                      Role
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help inline-block" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-md max-h-[400px] overflow-y-auto p-4" side="right">
                            <div className="space-y-3">
                              <div className="font-semibold text-sm mb-2">Available Roles:</div>
                              {roleOptions.map((role) => (
                                <div key={role.value} className="border-b border-border pb-2 last:border-0 last:pb-0">
                                  <div className="font-medium text-sm mb-1">{role.label}</div>
                                  {role.description && (
                                    <div className="text-xs text-muted-foreground mb-2">{role.description}</div>
                                  )}
                                  <div className="text-xs text-muted-foreground whitespace-pre-line">
                                    {getRolePermissionsDescription(role.permissions)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {roleOptions.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-6">
                <FormField control={form.control} name="teams" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Teams</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={teamOptions}
                        onValueChange={field.onChange}
                        value={field.value || []}
                        placeholder="Select teams"
                        maxCount={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>
}
